import React, { useRef, useState } from "react";
import { Mic, Square, Loader2, Sparkles } from "lucide-react";
import { sttTranscribe, refineSignal, createMissionsBatch } from "../lib/api";

export const MicButton = ({ onMissionsCreated, onTranscript }) => {
  const [recording, setRecording] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | recording | transcribing | refining | done
  const [transcript, setTranscript] = useState("");
  const [missions, setMissions] = useState([]);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await processBlob(blob);
      };
      mr.start();
      setRecording(true);
      setPhase("recording");
      setTranscript("");
      setMissions([]);
    } catch (e) {
      console.error("Mic access failed", e);
      alert("Microphone access was blocked. Please allow it in your browser.");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    setRecording(false);
  };

  const processBlob = async (blob) => {
    try {
      setPhase("transcribing");
      const stt = await sttTranscribe(blob, "voice.webm");
      const text = (stt.transcript || "").trim();
      setTranscript(text);
      onTranscript && onTranscript(text);
      if (!text) { setPhase("idle"); return; }
      setPhase("refining");
      const refined = await refineSignal(text);
      setMissions(refined.missions || []);
      // Auto-save missions
      const batch = await createMissionsBatch(refined.missions || [], "voice");
      onMissionsCreated && onMissionsCreated(batch.created || []);
      setPhase("done");
    } catch (e) {
      console.error(e);
      setPhase("idle");
      alert("The Refiner had trouble. Please try again.");
    }
  };

  const reset = () => {
    setPhase("idle"); setTranscript(""); setMissions([]);
  };

  return (
    <div className="w-full">
      <div className="flex flex-col items-center gap-4">
        <button
          type="button"
          onClick={recording ? stop : start}
          data-testid="voice-mic-button"
          aria-label={recording ? "Stop recording" : "Start voice signal"}
          disabled={phase === "transcribing" || phase === "refining"}
          className={`relative h-28 w-28 md:h-32 md:w-32 rounded-full flex items-center justify-center transition-all duration-300
            ${recording
              ? "bg-gradient-to-br from-red-500 to-red-700 eigoo-pulse-red scale-110"
              : "bg-gradient-to-br from-yellow-300 to-yellow-600 hover:scale-110 eigoo-pulse-gold"}
            ${(phase === "transcribing" || phase === "refining") ? "opacity-70 cursor-wait" : "cursor-pointer"}`}
        >
          {phase === "transcribing" || phase === "refining" ? (
            <Loader2 className="text-black" size={48} />
          ) : recording ? (
            <Square className="text-white" size={42} fill="currentColor" />
          ) : (
            <Mic className="text-black" size={48} strokeWidth={2.5} />
          )}
          {recording && (
            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-xs font-display font-bold text-red-300 bg-black/70 px-2 py-0.5 rounded-full border border-red-500/50">
              REC
            </span>
          )}
        </button>

        <div className="text-center">
          <p className="font-display font-bold text-lg md:text-xl text-yellow-300" data-testid="mic-status">
            {phase === "idle" && "Tap and speak your vision"}
            {phase === "recording" && "Listening, Commander..."}
            {phase === "transcribing" && "Decoding signal..."}
            {phase === "refining" && "Refining your missions..."}
            {phase === "done" && "Signal received. Missions deployed!"}
          </p>
          <p className="text-sm text-white/50 mt-1">
            Try: "Build a website. Make a poster. Practice 5 new words."
          </p>
        </div>

        {transcript && (
          <div className="w-full eigoo-glass rounded-2xl p-4 mt-2" data-testid="transcript-panel">
            <div className="flex items-center gap-2 text-yellow-300 text-xs font-display font-bold tracking-widest mb-1">
              <Sparkles size={14} /> RAW SIGNAL
            </div>
            <p className="text-white/90 italic">"{transcript}"</p>
          </div>
        )}

        {missions.length > 0 && phase === "done" && (
          <div className="w-full eigoo-glass rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-yellow-300 text-xs font-display font-bold tracking-widest">REFINED OUTPUT</div>
              <button onClick={reset} className="text-xs text-white/60 hover:text-white" data-testid="refiner-reset">clear</button>
            </div>
            <ul className="space-y-2">
              {missions.map((m, i) => (
                <li key={`${m.title}-${i}`} className="flex items-start gap-2 text-sm text-white/90" data-testid={`refined-mission-${i}`}>
                  <span className="mt-1 inline-block h-2 w-2 rounded-full bg-yellow-300" />
                  <div>
                    <div className="font-bold">{m.title}</div>
                    <div className="text-white/60 text-xs">{m.summary}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default MicButton;
